/**
 * 販売開始お知らせメール登録 (Joomla page `shop.newproduct_reg`).
 *
 * Flow 1:1 site cũ: nhập → 確認画面 (in-page) → POST → 完了.
 *   - `?group=&category=&product=` từ nút 「販売が開始したらメールで通知する」 trên PDP.
 *   - GET /new-product/group để hiện tên group + 状況 (khi có product_id).
 *   - Validate hiển thị nguyên văn message tiếng Nhật từ 422 `errors` của Laravel
 *     (đã port ở `RegisterNewProductNotifyRequest`) — KHÔNG tự viết message ở client.
 *   - Nút 登録 hiện overlay 処理中 (rule §Processing overlay).
 *
 * 画像認証 KHÔNG port — honeypot `notify_url` (ẩn) + rate limit server.
 */
(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  function boot() {
    const root = document.getElementById('ink-new-product-notify');
    if (!root) return;

    const form = root.querySelector('[data-np-form]');
    if (!form) return;

    const params = new URLSearchParams(window.location.search);
    const groupId = parseInt(params.get('group') || '0', 10);
    const categoryId = parseInt(params.get('category') || '0', 10);
    const productId = parseInt(params.get('product') || '0', 10);

    const steps = {
      input: root.querySelector('[data-np-step="input"]'),
      confirm: root.querySelector('[data-np-step="confirm"]'),
      done: root.querySelector('[data-np-step="done"]'),
    };
    const errorsBox = root.querySelector('[data-np-errors]');
    const confirmErrors = root.querySelector('[data-np-confirm-errors]');

    function showStep(name) {
      Object.keys(steps).forEach((k) => {
        if (steps[k]) steps[k].classList.toggle('hidden', k !== name);
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /** Hiện list lỗi — nguyên văn message server (site cũ cũng list `※...`). */
    function showErrors(box, messages) {
      if (!box) return;
      const list = (messages || []).filter(Boolean);
      if (!list.length) {
        box.classList.add('hidden');
        box.innerHTML = '';
        return;
      }
      box.innerHTML = list.map((m) => `<li>※${esc(m)}</li>`).join('');
      box.classList.remove('hidden');
    }

    function clearErrors() {
      showErrors(errorsBox, []);
      showErrors(confirmErrors, []);
    }

    /** 422 `errors` {field: [msg]} → list phẳng, giữ thứ tự name→kana→email→agree. */
    function flattenErrors(errors) {
      const order = ['name', 'kana', 'email', 'agree', 'group_id', 'notify_url'];
      const out = [];
      order.forEach((f) => {
        (errors[f] || []).forEach((m) => out.push(m));
      });
      Object.keys(errors).forEach((f) => {
        if (order.indexOf(f) === -1) (errors[f] || []).forEach((m) => out.push(m));
      });
      return out;
    }

    // ===== Nút キャンセル / 戻る về trang sản phẩm =====
    // So sánh ORIGIN chứ không phải indexOf(host): chuỗi host có thể nằm trong path
    // hoặc query của trang ngoài (vd `https://evil.example/?r=shop.myshopify.com`),
    // khi đó nút キャンセル/戻る mang vẻ nội bộ lại đưa khách ra ngoài.
    function sameOriginOrRoot(url) {
      if (!url) return '/';
      try {
        return new URL(url, window.location.href).origin === window.location.origin ? url : '/';
      } catch (e) {
        return '/';
      }
    }

    const backHref = sameOriginOrRoot(document.referrer);
    root.querySelectorAll('[data-np-cancel], [data-np-back-link]').forEach((a) => {
      a.setAttribute('href', backHref);
    });

    // ===== Tên group + 状況 =====
    async function loadGroup() {
      if (!productId || !window.inkApi) return;
      try {
        const res = await window.inkApi.get(`/new-product/group?product_id=${productId}`);
        const g = res && res.data;
        if (!g || !g.group_id) return;
        const box = root.querySelector('[data-np-group-box]');
        const nameEl = root.querySelector('[data-np-group-name]');
        const sitEl = root.querySelector('[data-np-situation]');
        if (nameEl) nameEl.textContent = g.group_name || '';
        if (sitEl) sitEl.textContent = g.situation || '';
        if (box) box.classList.remove('hidden');
      } catch (e) {
        // Fail-open: không có tiêu đề group vẫn đăng ký được
        console.warn('[new-product-notify] group fetch failed', e);
      }
    }

    // ===== Bước 1 → 2 =====
    let pending = null;

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      clearErrors();

      const data = {
        name: (form.querySelector('[name="name"]').value || '').trim(),
        kana: (form.querySelector('[name="kana"]').value || '').trim(),
        email: (form.querySelector('[name="email"]').value || '').trim(),
        agree: form.querySelector('[data-np-agree]').checked ? 'yes' : '',
        group_id: groupId,
        category_id: categoryId,
        product_id: productId,
        notify_url: form.querySelector('[name="notify_url"]').value || '',
      };

      // Client chỉ chặn rỗng để đỡ 1 round-trip; message CHUẨN vẫn từ server.
      const missing = [];
      if (!data.name) missing.push('お名前（漢字）をご入力ください。');
      if (!data.kana) missing.push('お名前（フリガナ）をご入力ください。');
      if (!data.email) missing.push('有効なアドレスを半角でご入力ください。');
      if (data.agree !== 'yes') missing.push('利用規約をお読み頂き、ご同意の上ご利用ください。');
      if (missing.length) {
        showErrors(errorsBox, missing);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      pending = data;
      root.querySelector('[data-np-review="name"]').textContent = data.name;
      root.querySelector('[data-np-review="kana"]').textContent = data.kana;
      root.querySelector('[data-np-review="email"]').textContent = data.email;
      showStep('confirm');
    });

    // ===== Bước 2 → 3 =====
    const submitBtn = root.querySelector('[data-np-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        if (!pending) return;
        clearErrors();
        if (!window.inkApi) {
          showErrors(confirmErrors, ['システムエラーが発生しました。大変お手数ですが、再度ご登録をお願い致します。']);
          return;
        }
        submitBtn.disabled = true;
        window.inkProcessing?.show('登録中...');
        try {
          const res = await window.inkApi.post('/new-product/notify', pending);
          if (res && res.data && res.data.registered) {
            showStep('done');
            return;
          }
          if (res && res.errors) {
            // Lỗi field (vd email đã đăng ký) → về form để sửa
            showStep('input');
            showErrors(errorsBox, flattenErrors(res.errors));
            return;
          }
          showErrors(confirmErrors, [
            (res && (res.message || (res.error && res.error.message)))
              || 'システムエラーが発生しました。大変お手数ですが、再度ご登録をお願い致します。',
          ]);
        } catch (e) {
          console.warn('[new-product-notify] submit failed', e);
          showErrors(confirmErrors, ['通信エラーが発生しました。通信環境をご確認のうえ再度お試しください。']);
        } finally {
          submitBtn.disabled = false;
          window.inkProcessing?.hide();
        }
      });
    }

    const backBtn = root.querySelector('[data-np-back]');
    if (backBtn) backBtn.addEventListener('click', () => { clearErrors(); showStep('input'); });

    loadGroup();
  }

  // api-client.js load cuối body → chờ event ink:api:ready (memory
  // project_inkapi_ready_theme_dev_verify), fallback load+1s.
  let booted = false;
  function startOnce() {
    if (booted) return;
    booted = true;
    boot();
  }
  if (window.inkApi) {
    startOnce();
  } else {
    document.addEventListener('ink:api:ready', startOnce, { once: true });
    window.addEventListener('load', () => {
      setTimeout(() => { if (!window.inkApi) startOnce(); }, 1000);
    });
  }
})();
